<script setup lang="ts">

import BaseButton from '@/components/base/BaseButton.vue'
import { T } from '@tolgee/vue'
import { computed, ref } from 'vue'
import DeleteAccountModal from '@/components/modals/DeleteAccountModal.vue'
import BaseToggle from '@/components/base/BaseToggle.vue'
import { useDeviceSettings, useNotificationSettings } from '@/composables/settings'
import DeleteAccountConfrimationModal from '@/components/modals/DeleteAccountConfrimationModal.vue'
import { SettingsNotificationType } from '@/types/shared/settings'
import WalletConnect from '@/components/WalletConnect.vue'
import { useSnapshot } from '@/composables/game/snapshot'

const { gameDeviceSettings, setCoinAnimation, setHapticFeedback } = useDeviceSettings()
const { notificationSettings, updateNotificationState } = useNotificationSettings()
const { isAfterSnapshot } = useSnapshot()

const showDeleteAccountModal = ref<boolean>(false)
const showDeleteAccountConfirmationModal = ref<boolean>(false)

// Gameplay settings
const withHapticFeedback = computed(() => gameDeviceSettings.value.hapticFeedback)
const showCoinAnimation = computed(() => gameDeviceSettings.value.coinAnimation)

// Notification settings
const notifyEnergyRefilled = computed(() => notificationSettings.value.energyRefilled)
const notifyOfflineProfit = computed(() => notificationSettings.value.offlineProfit)
const notifyReferral = computed(() => notificationSettings.value.referral)
const notifyPoked = computed(() => notificationSettings.value.poked) 

</script>
<template>
  <div class="flex flex-1 flex-col w-full h-full gap-y-10 justify-between">
    <div class="flex flex-col gap-y-6 w-full">
      <div
        class="w-full flex flex-col gap-y-3"
      >
        <div class="flex items-center gap-x-2">
          <img
            src="@/assets/images/money.png"
            class="size-6"
          >
          <p class="text-base font-medium tracking-tight text-white/60">
            <T
              key-name="wallet.title"
              default-value="Wallet"
            />
          </p>
        </div>
        <WalletConnect :button-has-icon="false" :disable-disconnect="isAfterSnapshot" />
      </div>

      <div class="flex flex-col gap-y-1.5">
        <div class="flex items-center gap-x-2">
          <img
            src="../assets/images/emojis/emoji-videogame.png"
            class="size-6"
          >
          <p class="text-base font-medium tracking-tight text-white/60">
            <T
              key-name="settings.gameplay"
              default-value="Gameplay"
            />
          </p>
        </div>

        <div class="flex flex-col gap-y-4 bg-dark-bottom-layer rounded-xl p-4">
          <div class="flex justify-between items-center w-full">
            <p class="text-base font-medium tracking-tight">
              <T
                key-name="settings.haptic_feedback"
                default-value="Haptic feedback"
              />
            </p>
            <BaseToggle
              :is-active="withHapticFeedback"
              @on-toggle="setHapticFeedback(!withHapticFeedback)"
            />
          </div>
          <div class="h-[1px] w-full bg-white/[12%]" />
          <div class="flex justify-between items-center w-full">
            <p class="text-base font-medium tracking-tight">
              <T
                key-name="settings.coins_animation"
                default-value="Coins animation"
              />
            </p>
            <BaseToggle
              :is-active="showCoinAnimation"
              @on-toggle="setCoinAnimation(!showCoinAnimation)"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-y-1.5">
        <div class="flex items-center gap-x-2">
          <img
            src="../assets/images/emojis/emoji-bell.png"
            class="size-6"
          >
          <p class="text-base font-medium tracking-tight text-white/60">
            <T
              key-name="settings.notifications"
              default-value="Notifications"
            />
          </p>
        </div>

        <div class="flex flex-col gap-y-4 bg-dark-bottom-layer rounded-xl p-4">
          <!--          <div class="flex justify-between items-center w-full">-->
          <!--            <p class="text-base font-medium tracking-tight">-->
          <!--              <T-->
          <!--                key-name="settings.notifications.energy"-->
          <!--                default-value="Energy refilled"-->
          <!--              />-->
          <!--            </p>-->
          <!--            <BaseToggle-->
          <!--              :is-active="notifyEnergyRefilled"-->
          <!--              @on-toggle="updateNotificationState(SettingsNotificationType.ENERGY_REFILLED, !notifyEnergyRefilled)"-->
          <!--            />-->
          <!--          </div>-->
          <!--          <div class="h-[1px] w-full bg-white/[12%]" />-->
          <!--          <div class="flex justify-between items-center w-full">-->
          <!--            <p class="text-base font-medium tracking-tight">-->
          <!--              <T-->
          <!--                key-name="settings.notifications.offline"-->
          <!--                default-value="Offline earnings full"-->
          <!--              />-->
          <!--            </p>-->
          <!--            <BaseToggle-->
          <!--              :is-active="notifyOfflineProfit"-->
          <!--              @on-toggle="updateNotificationState(SettingsNotificationType.OFFLINE_PROFIT, !notifyOfflineProfit)"-->
          <!--            />-->
          <!--          </div>-->
          <!--          <div class="h-[1px] w-full bg-white/[12%]" />-->
          <div class="flex justify-between items-center w-full">
            <p class="text-base font-medium tracking-tight">
              <T
                key-name="settings.notifications.friend"
                default-value="Friend added"
              />
            </p>
            <BaseToggle
              :is-active="notifyReferral"
              @on-toggle="updateNotificationState(SettingsNotificationType.REFERRAL, !notifyReferral)"
            />
          </div>
          <div class="h-[1px] w-full bg-white/[12%]" />
          <div class="flex justify-between items-center w-full">
            <p class="text-base font-medium tracking-tight">
              <T
                key-name="settings.notifications.poked"
                default-value="Poked"
              />
            </p>
            <BaseToggle
              :is-active="notifyPoked"
              @on-toggle="updateNotificationState(SettingsNotificationType.POKE, !notifyPoked)"
            />
          </div>
        </div>
      </div>
    </div>
    <BaseButton
      style-type="tertiary"
      @click="showDeleteAccountModal = true"
    >
      <T
        key-name="settings.delete_account"
        default-value="Delete account"
      />
    </BaseButton>
  </div>

  <DeleteAccountModal
    v-if="showDeleteAccountModal"
    :show="showDeleteAccountModal"
    @hide="showDeleteAccountModal = false"
    @on-delete="showDeleteAccountConfirmationModal = true"
  />

  <DeleteAccountConfrimationModal
    v-if="showDeleteAccountConfirmationModal"
    :show="showDeleteAccountConfirmationModal"
    centered
    @hide="showDeleteAccountConfirmationModal = false; showDeleteAccountModal = false"
  />
</template>
